import React from "react";
import { Box, Card, CircularProgress } from "@mui/joy";
import DarkTypography from "../../common/DarkTypography";
import type { MenuTileUi } from "../../../api/zekay/menu";
import {
  colorTokens,
  ctaAccentColor,
  menuTileBadgeRingSx,
  menuTileCardSx,
} from "../../../styles/globalStyles";

type Props = {
  Tile: MenuTileUi;
  BadgeCount?: number;
  BadgeLoading?: boolean;
  OnClick: () => void;
};

export default function MenuTileCard({
  Tile,
  BadgeCount = 0,
  BadgeLoading = false,
  OnClick,
}: Props) {
  const showCount = !BadgeLoading && BadgeCount > 0;
  const showLoading = BadgeLoading;
  const showAnyBadge = showCount || showLoading;
  const label = BadgeCount > 99 ? "99+" : String(BadgeCount);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      OnClick();
    }
  };

  return (
    <Card
      onClick={OnClick}
      onKeyDown={handleKeyDown}
      variant="plain"
      sx={menuTileCardSx}
      role="button"
      tabIndex={0}
      aria-label={Tile.label}
    >
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          lineHeight: 0,
        }}
      >
        {React.cloneElement(Tile.icon as React.ReactElement, {
          sx: { fontSize: { xs: 32, sm: 36 }, color: ctaAccentColor },
        })}
      </Box>
      {showAnyBadge && (
        <Box
          sx={{
            ...menuTileBadgeRingSx,
            position: "absolute",
            top: 10,
            right: 10,
            minWidth: showCount ? 22 : 14,
            height: showCount ? 22 : 14,
            px: showCount ? 0.75 : 0,
            borderRadius: showCount ? 999 : "50%",
            backgroundColor: colorTokens.status.danger,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {showLoading ? (
            <CircularProgress
              size="sm"
              sx={{
                "--CircularProgress-size": "18px",
                color: colorTokens.text.primary,
              }}
            />
          ) : (
            <DarkTypography
              level="body-xs"
              colorVariant="primary"
              component="span"
              sx={{ fontWeight: 800, lineHeight: 1 }}
            >
              {label}
            </DarkTypography>
          )}
        </Box>
      )}
      <DarkTypography
        colorVariant="secondary"
        level="body-md"
        sx={{
          flex: 1,
          minWidth: 0,
          textAlign: "left",
          fontWeight: 700,
          fontSize: { xs: "0.9375rem", sm: "1rem" },
          lineHeight: 1.3,
          whiteSpace: "normal",
          wordBreak: "normal",
        }}
      >
        {Tile.label}
      </DarkTypography>
    </Card>
  );
}
